import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useCallback, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { useRefetchOnScreenFocus } from "@repo/core/hooks/useRefetchOnScreenFocus";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { mapProposalsGroupedByDate } from "@repo/core/mappers/proposals";
import { Proposals } from "@repo/core/components/layout/ProposalGroup/types";
import useAuthStore from "@repo/core/stores/auth";

import {
  buildProposalSearchQueryParams,
  filterByCategory,
  filterEligibleCpfSearchProposals,
  filterVisibleInAnalysisQueue,
  getProposalOptionsBySituationCode,
  isCompleteCpf,
  isProposalSearchNotFoundError,
  shouldHideProposalStatusFilters
} from "./utils";
import {
  extractProposalSearchItems,
  mapProposalSearchResultsToGroups
} from "./mappers";
import { IProposalOptionsSheetRef } from "./components/ProposalOptionsSheet/types";
import { usePreProposal } from "@repo/core/screens/PreProposal/hooks/usePreProposal";
import { ProposalCardProps } from "@repo/core/components/layout/ProposalCard/types";
import { useUserPermissions } from "@repo/core/hooks/useUserPermissions";

const proposalsResource = new ProposalsResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);

export const ProposalsSearchController = () => {
  const actionSheetRef = useRef<IProposalOptionsSheetRef>(null);
  const isStartingPreProposalRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isStartingPreProposal, setIsStartingPreProposal] = useState(false);

  // Local filter state (replacing useFilterStore temporarily)
  const [searchText, setSearchText] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const { startPreProposal } = usePreProposal();
  const { permissions } = useUserPermissions();
  const { user } = useAuthStore();

  const { isMobile } = useDimensions();
  const { isWeb } = useDeviceType();

  const cpfSearchParams = useMemo(
    () =>
      buildProposalSearchQueryParams({
        lstProdutos: user?.tokenData?.lstProdutos,
        searchText
      }),
    [searchText, user?.tokenData?.lstProdutos]
  );
  const isCpfSearchActive = isCompleteCpf(searchText);
  const hideStatusFilters = shouldHideProposalStatusFilters(searchText);

  const {
    data: proposalsData,
    refetch,
    isFetching,
    isLoading,
    dataUpdatedAt
  } = useQuery({
    queryKey: ["proposalsSearch"],
    queryFn: () => proposalsResource.consultProposals(),
    staleTime: 0,
    refetchOnMount: "always"
  });

  const {
    data: cpfSearchResults,
    refetch: refetchCpfSearch,
    isFetching: isCpfSearchFetching,
    isError: isCpfSearchError,
    dataUpdatedAt: cpfSearchUpdatedAt
  } = useQuery({
    queryKey: ["proposalsSearchByCpf", cpfSearchParams],
    queryFn: async () => {
      if (!cpfSearchParams) {
        return [];
      }

      try {
        const { data } =
          await requisitionsResource.searchProposalsByCpf(cpfSearchParams);
        return extractProposalSearchItems(data);
      } catch (error) {
        if (isProposalSearchNotFoundError(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: Boolean(cpfSearchParams),
    staleTime: 0,
    refetchOnMount: "always"
  });

  const refetchProposals = useCallback(() => {
    if (isCpfSearchActive) {
      void refetchCpfSearch();
      return;
    }

    void refetch();
  }, [isCpfSearchActive, refetch, refetchCpfSearch]);

  useRefetchOnScreenFocus(refetchProposals);

  const queueProposals: Proposals[] = useMemo(() => {
    if (!proposalsData?.data) {
      return [];
    }

    return filterVisibleInAnalysisQueue(
      mapProposalsGroupedByDate(proposalsData.data)
    );
  }, [proposalsData]);

  const searchedProposals: Proposals[] = useMemo(() => {
    if (!isCpfSearchActive) {
      return [];
    }

    return filterEligibleCpfSearchProposals(
      mapProposalSearchResultsToGroups(cpfSearchResults)
    );
  }, [cpfSearchResults, isCpfSearchActive]);

  const filteredProposals: Proposals[] = useMemo(() => {
    if (isCpfSearchActive) {
      return searchedProposals;
    }

    if (selectedCategory) {
      return filterByCategory(queueProposals, selectedCategory);
    }

    return queueProposals;
  }, [isCpfSearchActive, queueProposals, searchedProposals, selectedCategory]);

  const listIsFetching = isCpfSearchActive ? isCpfSearchFetching : isFetching;
  const listIsLoading = isCpfSearchActive
    ? isCpfSearchFetching ||
      (cpfSearchResults === undefined && !isCpfSearchError)
    : isLoading;
  const listUpdatedAt = isCpfSearchActive ? cpfSearchUpdatedAt : dataUpdatedAt;

  const onRefresh = useCallback(async () => {
    setRefreshing(true);

    try {
      await (isCpfSearchActive ? refetchCpfSearch() : refetch());
    } finally {
      setRefreshing(false);
    }
  }, [isCpfSearchActive, refetch, refetchCpfSearch]);

  const handleStartPreProposal = useCallback(async () => {
    if (isStartingPreProposalRef.current) {
      return;
    }

    isStartingPreProposalRef.current = true;
    setIsStartingPreProposal(true);

    try {
      await startPreProposal();
    } finally {
      isStartingPreProposalRef.current = false;
      setIsStartingPreProposal(false);
    }
  }, [startPreProposal]);

  const handleOpenOptions = useCallback(
    (id: string, statusCode: string, proposalTypeCode?: string) => {
      actionSheetRef.current?.open(id, statusCode, proposalTypeCode);
    },
    []
  );

  const shouldShowOptions = useCallback(
    (proposal: ProposalCardProps) =>
      getProposalOptionsBySituationCode(
        proposal.statusCode,
        proposal.proposalTypeCode,
        undefined,
        permissions
      ).length > 0,
    [permissions]
  );

  const marginBottomFab = isMobile && isWeb ? "$16" : "$2";

  const marginRightFab = useMemo(() => {
    if (isMobile && isWeb) {
      return "$0";
    }

    if (!isMobile && isWeb) {
      return "$6";
    }

    return "$0";
  }, [isMobile, isWeb]);

  const lastUpdated = listUpdatedAt ? new Date(listUpdatedAt) : new Date();

  const updatedText = useMemo(() => {
    if (listIsFetching) return "Carregando...";
    return `Atualizado em ${format(lastUpdated, "dd/MM 'às' HH:mm:ss", { locale: ptBR })}`;
  }, [listIsFetching, lastUpdated]);

  return {
    proposals: filteredProposals,
    marginBottomFab,
    actionSheetRef,
    handleOpenOptions,
    shouldShowOptions,
    onRefresh,
    refreshing: refreshing,
    isFetching: listIsFetching,
    isLoading: listIsLoading,
    lastUpdated,
    updatedText,
    marginRightFab,
    startPreProposal: handleStartPreProposal,
    isStartingPreProposal,
    isMobile,
    // Filter state and setters
    searchText,
    setSearchText,
    selectedCategory,
    setSelectedCategory,
    hideStatusFilters
  };
};
